import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SourcingStatus } from '@tailoring-crm/shared';
import { Badge, type BadgeProps } from '@tailoring-crm/ui';

/**
 * Where one requirement stands, in a word.
 *
 * The word is never carried by the colour alone — every badge says what it is. `cancelled` reads as
 * quiet rather than as a failure: a need that went away because the customer changed the design is
 * not a problem anybody has to fix, and colouring it red would put four alarming rows on a card
 * where nothing is wrong.
 */
const VARIANT_OF: Readonly<Record<SourcingStatus, NonNullable<BadgeProps['variant']>>> =
  Object.freeze({
    to_start: 'outline',
    in_progress: 'default',
    received: 'success',
    cancelled: 'secondary',
  });

export function SourcingStatusBadge({ status }: { status: SourcingStatus }): ReactElement {
  const { t } = useTranslation();
  return <Badge variant={VARIANT_OF[status]}>{t(`sourcing.status.${status}`)}</Badge>;
}

export default SourcingStatusBadge;
